import ComponentBase from "@components/ComponentBase";
import { ComponentElem } from "@components/interfaces";
import PickDateRange, { PickDateRangeResult } from "@components/popups/PickDateRange";
import ApiProxy from "@ts/ApiProxy";
import Tools from "@ts/Tools";
import PlanOption from "./PlanOption";
import PlanResult from "./PlanResult";
import { Product } from "./ProductTypes";
import GS from "@ts/GlobalSetting";
import schema from "@schema/product/get-plan-date-list.json";

export default class PlanSelect extends ComponentBase {
  _priceClassDic: any = {};
  pickedDate: moment.Moment;
  pickedDateId: string;
  pickedDateText: string;
  pickedPlanOption: PlanOption;
  pageData: Product.PageData;
  planOptionList: PlanOption[];
  planOptionDic: { [key: string]: PlanOption };
  isDatePicked: boolean;
  $dateTitle: JQuery;
  $dateText: JQuery;
  private _planResult: PlanResult;
  private _fetchedMonthsDic: { [key: string]: boolean } = {};

  constructor(elem: ComponentElem) {
    super(elem);
    this.pageData = ApiProxy.getPageData();
    this.pageData.date_dic = {};

    this.pageData.plan_dic = Tools.listToDic(this.pageData.plan_data, "id");

    this.pageData.plan_data.forEach((planData) => {
      let unitData = planData.unit_data[0];
      planData.display_price = unitData.promote_price ? unitData.promote_price : unitData.price;
      this._addPriceClass(String(planData.display_price));

      planData.time_dic = Tools.listToDic(planData.time_data, "id");
      planData.unit_dic = Tools.listToDic(planData.unit_data, "id");
    });

    this._resetOptions();
    this._setupDatePicker();

    this._planResult = (this.$root.find(`[component="plan-result"]`)[0] as ComponentElem)._component;
  }

  update() {
    let feedback = this._planResult.update(
      this.pickedDateId,
      this.pickedDateText,
      this.pickedPlanOption
    );

    this.$root.find(".title-request-plan").attr("request-completed", feedback.planCompleted ? "true" : "false");
  }

  applyCartItem() {
    let pageData: Product.PageData = ApiProxy.getPageData();
    let cartRecord: Product.CartRecord = pageData.cart_record;
    if (cartRecord) {
      ///@ts-ignore
      let date = moment(cartRecord.plan_data.date, GS.DATE_FORMAT);
      let year = date.year();
      let month = date.month() + 1;

      this.$root.css({
        "opacity": ".5",
        "pointer-events": "none"
      });

      this._fetchPlanDateList(year, month, false).then(() => {
        this.$root.css({
          "opacity": "",
          "pointer-events": ""
        });

        let planData: Product.PlanData = pageData.plan_dic[cartRecord.plan_data.plan_id];
        if (!planData)
          throw new Error(`cart_record.plan_data.plan_id: [${cartRecord.plan_data.plan_id}] matching fail`);

        let dateData = pageData.date_dic[cartRecord.plan_data.date];
        if (!dateData)
          throw new Error(`cart_record.plan_data.date: [${cartRecord.plan_data.date}] matching fail`);

        let timeData = planData.time_dic[cartRecord.plan_data.time_id];
        if (!timeData)
          throw new Error(`cart_record.plan_data.time_id: [${cartRecord.plan_data.time_id}] matching fail`);

        this._pickDateByString(cartRecord.plan_data.date);
        this._pickOptionAndTime(cartRecord.plan_data.plan_id, cartRecord.plan_data.time_id);

        cartRecord.plan_data.unit_data.forEach((data: { id: string, count: number }) => {
          let unitData: Product.UnitData = planData.unit_dic[data.id];
          if (!unitData)
            throw new Error(`cart_record.plan_data.unit_list, id: [${data.id}] matching fail`);
          this.pickedPlanOption.setUnitCountValue(data.id, data.count);
        });
        this.update();
      })
    }
  }

  private _addPriceClass(price: string) {
    if (this._priceClassDic[price])
      return;
    this._priceClassDic[price] = true;
    Tools.addCustomStyle(".price-" + price + "::after", 'content: "' + price + '"!important;');
  }

  private _setupDatePicker() {
    this.isDatePicked = false;
    this.$dateTitle = this.$root.find(".title-request-date");

    let $btnDateSelect: JQuery = this.$root.find(".btn-pick-date");
    let $btnTextSpan = this.$dateText = $('<span class="button-text"></span>');
    let $dateInput = $(document.createElement("input")).toggleClass("date-input-placeholder", true);

    $btnTextSpan.text($btnDateSelect.text());
    $btnDateSelect.text("");
    $btnDateSelect.append($btnTextSpan);
    $btnDateSelect.append($dateInput);

    $btnDateSelect.on("click", (event: Event) => {
      event.preventDefault();
      this._triggerPickDateUI($btnDateSelect);
    });
    return;
  }

  private _triggerPickDateUI($btnDateSelect: JQuery) {
    PickDateRange.instance.requestSingle(
      $btnDateSelect[0],
      this.pickedDate,
      null,
      {
        showDropdowns: true,
        onMonthChange: (year: number, month: number) => {
          this._fetchPlanDateList(year, month)
        },
        isInvalidDate: (date: moment.Moment, end: moment.Moment): boolean => {
          return !this.pageData.date_dic[date.format(GS.DATE_FORMAT)];
        }
      }).then((result: PickDateRangeResult) => {
        if (!result)
          return;
        if (result.start) {
          this._pickDate(result.start);
          this._planResult.clearRecord();
        }
      });
  }

  private _fetchPlanDateList(year: number, month: number, isForPickDate: boolean = true) {
    return new Promise((resolve) => {
      let key = `${year}-${month}`;
      let params = { product_id: this.pageData.id, year: year, month: month };
      let exampleIndex = 0;
      if (this._fetchedMonthsDic[key])
        return;
      this._fetchedMonthsDic[key] = true;
      if (isForPickDate)
        PickDateRange.instance.togglePending(true);
      if (year === 2024 && month === 1)
        exampleIndex = 1;
      if (year === 2023 && month === 10)
        exampleIndex = 2;
      if (year === 2023 && month === 4)
        exampleIndex = 3;

      ApiProxy.quickCall2("product/get-plan-date-list", params, { schema: schema, exampleIndex: exampleIndex })
        .then((result: any) => {
          let dic = Tools.listToDic(result.data.date_list, "date");
          this.pageData.date_dic = Object.assign(this.pageData.date_dic, dic);
          if (isForPickDate) {
            PickDateRange.instance.update();
            PickDateRange.instance.togglePending(false);
          }
          resolve(true);
        });
    });
  }

  _pickDate(date: moment.Moment) {
    let dateData = this.pageData.date_dic[date.format(GS.DATE_FORMAT)];
    if (dateData) {
      this.pickedDate = date;
      this.pickedDateId = date.format(GS.DATE_FORMAT);
      this.pickedDateText = this.$dateText[0].textContent = date.locale("zh-tw").format("LL");
      this.isDatePicked = true;
      this.$dateTitle.attr("request-completed", "true");
      this._resetOptions();
      this.update();
    }
  }

  _pickDateByString(dateString: string) {
    ///@ts-ignore
    let date = moment(dateString, GS.DATE_FORMAT);
    this._pickDate(date);
  }

  _resetOptions() {
    this.pickedPlanOption = null;
    this.planOptionList = [];

    let $options = this.$root.find(".options").empty();

    let list: Product.PlanData[] | Product.AvailablePlanData[] = this.pageData.plan_data;
    let dateData: Product.DateData;
    let timeDataIdList: string[] = [];

    if (this.pickedDateId) {
      dateData = this.pageData.date_dic[this.pickedDateId];
      list = dateData.available_plan_list;
    }


    list.forEach((data) => {
      let id = data.id;
      let planData: Product.PlanData = this.pageData.plan_dic[id];

      if (this.pickedDateId)
        timeDataIdList = (data as Product.AvailablePlanData).available_time_list;

      let planOption = PlanOption.generate(planData, timeDataIdList, this.pickedDateId);

      this.planOptionList.push(planOption);
      planOption.appendTo($options);

      if (this.pickedDateId) {
        planOption.setState("idle");
        planOption.emitter.on("select", (isSelected: boolean) => {
          if (isSelected) {
            this._resetSelected("hide");
            planOption.setState("selected");
            this.pickedPlanOption = planOption;
          } else {
            this._resetSelected("idle");
            this.pickedPlanOption = null;
            planOption.reset();
            this._planResult.clearRecord();
          }
          this.update();
        });

        planOption.emitter.addListener("time-select", () => {
          this.update();
        });

        planOption.emitter.addListener("count-change", () => {
          this.update();
        });
      }
    });
  }

  _pickOptionAndTime(planId: string, timeId: string) {
    let planOption: PlanOption;
    this.planOptionList.every((obj: PlanOption) => {
      if (obj.data.id === planId) {
        planOption = obj;
        return false;
      }
      return true;
    });

    if (planOption) {
      this._resetSelected("hide");
      planOption.setState("selected");
      this.pickedPlanOption = planOption;
      planOption.timeSelect.selectTime(timeId);
      this.update();
    } else {
      throw new Error();
    }
  }

  private _resetSelected(state: "hide" | "idle") {
    this.planOptionList.forEach((planOption: PlanOption) => {
      planOption.setState(state);
    });
  }
}